import React, { useMemo, useState } from 'react';
import FamilyDialog from './FamilyDialog';
import type { FamilyToolsMember } from './types';
import { callManageFamily } from './manageFamilyClient';

interface AddToFamilyDialogProps {
  /** The member being added to someone else's family. */
  member: FamilyToolsMember;
  /** The full roster, used to search for a target family by member name/number. */
  allMembers: FamilyToolsMember[];
  onClose: () => void;
  onDone: (result: { message: string; changedMemberIds: string[] }) => void;
}

/** One member per represented family, so searching finds a family once, not once per member in it. */
function familyRepresentatives(allMembers: FamilyToolsMember[], excludeMemberId: string): FamilyToolsMember[] {
  const seen = new Set<string>();
  const reps: FamilyToolsMember[] = [];
  for (const m of allMembers) {
    if (m.id === excludeMemberId || !m.familyGroupId) continue;
    if (seen.has(m.familyGroupId)) continue;
    seen.add(m.familyGroupId);
    // Prefer the primary as the label for the family, falling back to whoever matched first.
    const primary = allMembers.find((x) => x.familyGroupId === m.familyGroupId && x.familyPrimary);
    reps.push(primary || m);
  }
  return reps;
}

const AddToFamilyDialog: React.FC<AddToFamilyDialogProps> = ({ member, allMembers, onClose, onDone }) => {
  const [search, setSearch] = useState('');
  const [targetFamilyGroupId, setTargetFamilyGroupId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const representatives = useMemo(() => familyRepresentatives(allMembers, member.id), [allMembers, member.id]);

  const matches = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return representatives;
    return representatives.filter(
      (m) =>
        `${m.firstName} ${m.lastName}`.toLowerCase().includes(term) ||
        (m.memberNumber || '').toLowerCase().includes(term)
    );
  }, [representatives, search]);

  const handleSubmit = async () => {
    if (!targetFamilyGroupId) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await callManageFamily('GROUP', {
        memberIds: [member.id],
        primaryMemberId: member.id,
        targetFamilyGroupId,
      });
      if (result.success) {
        onDone({
          message: result.message || `${member.firstName} ${member.lastName} was added to the family.`,
          changedMemberIds: result.changedMemberIds || [],
        });
      } else {
        setServerError(result.message || 'Could not add this member to the family. Please try again.');
      }
    } catch (err: any) {
      setServerError(err?.message || 'Could not add this member to the family. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FamilyDialog
      titleId="add-to-family-title"
      title="Add to a family"
      descriptionId="add-to-family-desc"
      busy={submitting}
      onClose={onClose}
      widthClassName="max-w-lg"
    >
      <p id="add-to-family-desc" className="mt-1 text-sm text-gray-600">
        Search for the family {member.firstName} {member.lastName} should join. Their member
        number is kept, and no payment is created.
      </p>

      {serverError && (
        <p role="alert" className="mt-3 rounded-md border border-red-200 bg-red-50 p-2 text-sm font-medium text-red-700">
          {serverError}
        </p>
      )}

      <div className="mt-4">
        <label htmlFor="atf-search" className="block text-sm font-medium text-gray-700 mb-1">
          Search members
        </label>
        <input
          id="atf-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name or member number"
          className="min-h-[44px] w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-fishing-blue"
        />
      </div>

      <ul className="mt-3 max-h-60 overflow-y-auto rounded-lg border border-gray-200" role="listbox" aria-label="Matching families">
        {matches.length === 0 ? (
          <li className="p-3 text-sm text-gray-500">No matching families found.</li>
        ) : (
          matches.map((m) => (
            <li key={m.familyGroupId}>
              <button
                type="button"
                role="option"
                aria-selected={targetFamilyGroupId === m.familyGroupId}
                onClick={() => setTargetFamilyGroupId(m.familyGroupId || null)}
                className={`min-h-[44px] w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-fishing-blue ${
                  targetFamilyGroupId === m.familyGroupId ? 'bg-blue-50 font-semibold text-fishing-blue' : 'text-gray-800'
                }`}
              >
                {m.lastName} Family ({m.firstName} {m.lastName}
                {m.memberNumber ? `, ${m.memberNumber}` : ''})
              </button>
            </li>
          ))
        )}
      </ul>

      <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !targetFamilyGroupId}
          className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Adding…' : 'Add to this family'}
        </button>
      </div>
    </FamilyDialog>
  );
};

export default AddToFamilyDialog;
